import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  DEFAULT_CONTENT_WIDTH,
  HERO_SECTION_BACKGROUND_VIDEO,
  HERO_SECTION_FALLBACK_IMAGE,
  HERO_SECTION_PLACEHOLDER_IMAGE,
} from '@/features/marketplace/constants';
import { SearchBarHero } from './components/search-bar-hero';
import { SearchRoomContextProvider } from '@/features/marketplace/hooks/use-search-room';
import { CitiesListContextProvider } from '@/features/marketplace/hooks/use-get-cities';
import { useTranslation } from 'react-i18next';

const HeroSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { t } = useTranslation();
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;

    return () => {
      video.muted = true;
      video.volume = 0;
      video.pause();
    };
  }, []);

  return (
    <section
      className={clsx(
        'relative flex min-h-screen items-center justify-center',
        'bg-cover bg-center',
        'text-white',
        'z-30'
      )}
    >
      {/* Background Video */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {!videoError && (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="h-full w-full object-cover"
            src={HERO_SECTION_BACKGROUND_VIDEO}
            poster={HERO_SECTION_PLACEHOLDER_IMAGE}
            aria-hidden="true"
            onError={() => setVideoError(true)}
            style={{
              transform: 'scale(1.5)',
            }}
          />
        )}
        {videoError && HERO_SECTION_FALLBACK_IMAGE && (
          <img
            src={HERO_SECTION_FALLBACK_IMAGE}
            alt=""
            className="h-full w-full object-cover"
            aria-hidden="true"
            style={{
              transform: 'scale(1.5)',
            }}
          />
        )}
      </div>
      {/* Overlay */}
      <div
        style={{ background: '#12022FA6 65%' }}
        className="absolute inset-0 z-10 opacity-60"
      ></div>

      {/* Content */}
      <div
        className={clsx(
          'relative z-10 flex w-full flex-col items-center justify-center',
          'mt-2 p-4',
          'text-center',
          DEFAULT_CONTENT_WIDTH
        )}
      >
        <div className="h-[12.8rem] lg:hidden" />

        <h1 className={clsx('text-[38px] font-bold leading-tight text-white md:text-[72px]')}>
          {t('js.features.marketplace.home.hero_section.title_part1')}
          <br />
          {t('js.features.marketplace.home.hero_section.title_part2')}
        </h1>
        <p className={clsx('mt-3 block text-[18px] font-bold text-white lg:text-[24px]')}>
          {t('js.features.marketplace.home.hero_section.subtitle')}
        </p>
        <div className="h-[12.8rem] lg:hidden" />

        {/* Search bar - styling might need to be adjusted within SearchBarHero component */}
        <div className="mt-8 w-full">
          <SearchRoomContextProvider>
            <CitiesListContextProvider>
              <SearchBarHero />
            </CitiesListContextProvider>
          </SearchRoomContextProvider>
        </div>
        <div
          className={clsx('relative flex w-full flex-1 justify-end', 'mt-[7.2rem] lg:mt-[0px]')}
        />
      </div>
    </section>
  );
};

export { HeroSection };
